import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AUTH_MODE } from '../../authConfig';

// Sidebar defaults:
// - Desktop/tablet: medium "icon" view (collapsed)
// - Small screens: peek strip
const getDefaultNavMode = () => (window.innerWidth < 640 ? 'peek' : 'collapsed');

// Ensure a deterministic default BEFORE any admin pages read localStorage during initial render.
// This runs at module-load time (Admin pages import AdminNav), so their layout offsets match immediately.
(() => {
  try {
    if (typeof window === 'undefined') return;
    const existing = localStorage.getItem('adminNavMode');
    if (!existing) {
      localStorage.setItem('adminNavMode', getDefaultNavMode());
    }
  } catch (_) {
    // ignore (e.g., storage blocked)
  }
})();

const AdminNav = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State: 'expanded' | 'collapsed' | 'peek'
  const [navMode, setNavMode] = useState(() => {
    const saved = localStorage.getItem('adminNavMode');
    if (saved) return saved;
    // Default: medium icon view on desktop, peek on mobile
    return getDefaultNavMode();
  });

  // On a true page refresh (or tab close), reset to defaults next time.
  // This preserves user choice while navigating inside the SPA, but guarantees refresh starts at icon view.
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        localStorage.removeItem('adminNavMode');
      } catch (_) {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleLogout = () => {
    if (AUTH_MODE === 'password') {
      localStorage.removeItem('adminAuthenticated');
      localStorage.removeItem('adminAuthTime');
      navigate('/');
    } else {
      console.log('Entra ID logout');
      navigate('/');
    }
  };

  useEffect(() => {
    localStorage.setItem('adminNavMode', navMode);
    // Backward compatibility: isCollapsed is true for 'collapsed' OR 'peek'
    const isCollapsed = navMode !== 'expanded';

    window.dispatchEvent(new CustomEvent('adminNavToggle', {
      detail: {
        isCollapsed, // Keep for backward compat
        mode: navMode // New specific mode
      }
    }));
  }, [navMode]);

  const navItems = [
    { path: '/admin/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/admin/approvals', icon: '🏷️', label: 'Tag Leads' },
    { path: '/admin/emails', icon: '📧', label: 'Email Digests' },
    { path: '/admin/regional-officers', icon: '👔', label: 'Regional Leadership' },
    { path: '/admin/campaigns', icon: '🏆', label: 'Campaigns' },
    { path: '/admin/conversions', icon: '💰', label: 'ROI Metrics' },
    { path: '/admin/analytics', icon: '📈', label: 'Analytics' },
    { path: '/admin/users', icon: '👥', label: 'Users' }
  ];

  const isActive = (path) => location.pathname === path;

  // Width calculation
  const getWidthClass = () => {
    switch (navMode) {
      case 'peek': return 'w-4 hover:w-6'; // Very thin strip
      case 'collapsed': return 'w-20';
      default: return 'w-64';
    }
  };

  const isPeek = navMode === 'peek';
  const isCollapsed = navMode === 'collapsed';
  // Combined "not expanded" for some logic
  const isCompact = navMode !== 'expanded';

  return (
    <div
      className={`fixed left-0 top-0 h-screen bg-[var(--pbfs-navy)] border-r border-white/10 flex flex-col transition-all duration-300 z-40 ${getWidthClass()} ${isPeek ? 'overflow-hidden cursor-pointer hover:bg-white/5' : ''}`}
      onClick={() => {
        // If in peek mode, clicking the strip expands it to collapsed (icons)
        if (isPeek) setNavMode('collapsed');
      }}
      title={isPeek ? "Click to show menu" : ""}
    >
      {/* Peek Indicator - Visualization of the "edge" */}
      {isPeek && (
        <div className="h-full w-full flex items-center justify-center">
          <div className="h-12 w-1 bg-white/20 rounded-full"></div>
        </div>
      )}

      {/* Main Content (Hidden in Peek) */}
      <div className={`flex flex-col h-full ${isPeek ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>

        {/* Header */}
        <div className={`p-6 border-b border-white/10 ${isCompact ? 'px-4' : ''}`}>
          <div className="flex items-center gap-3">
            <span className="text-3xl">🔥</span>
            {!isCompact && (
              <div>
                <h2 className="text-xl font-bold text-white">TagQuest</h2>
                <p className="text-sm text-white/60">Admin Panel</p>
              </div>
            )}
          </div>

          {/* Toggle Controls */}
          {navMode === 'expanded' ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNavMode('collapsed');
              }}
              className="mt-4 w-full flex items-center justify-center py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-white/60 hover:text-white"
              title="Collapse sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          ) : (
            /* Collapsed Mode: Split Controls (Peek | Expand) */
            <div className="mt-4 flex items-center justify-between gap-1">
              {/* Retract to Peek */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNavMode('peek');
                }}
                className="flex-1 flex items-center justify-center py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-white/60 hover:text-white"
                title="Minimize (Peek)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
              </button>

              {/* Expand to Full */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNavMode('expanded');
                }}
                className="flex-1 flex items-center justify-center py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-white/60 hover:text-white"
                title="Expand sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto overflow-x-hidden">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive(item.path)
                ? 'bg-[var(--pbfs-green)] text-white font-semibold shadow-lg'
                : 'text-white/70 hover:bg-white/10 hover:text-white'
                } ${isCompact ? 'justify-center' : ''}`}
            >
              <span className="text-xl shrink-0">{item.icon}</span>
              {!isCompact && <span className="whitespace-nowrap">{item.label}</span>}
            </Link>
          ))}

        </nav>

        {/* Footer - Sleek Utility Actions */}
        <div className="mt-auto border-t border-white/10">
          {/* Back to App - Prominent */}
          <Link
            to="/"
            className={`flex items-center gap-3 px-4 py-3 bg-[var(--pbfs-blue)]/10 hover:bg-[var(--pbfs-blue)]/20 border-b border-white/10 transition-all text-white hover:text-white ${isCompact ? 'justify-center' : ''}`}
            title="Back to TagQuest App"
          >
            <span className="text-xl shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </span>
            {!isCompact && <span className="whitespace-nowrap font-semibold">Back to App</span>}
          </Link>

          {/* Utility Row: Archive & Trash */}
          <div className="flex items-center border-b border-white/10">
          <Link
            to="/admin/archive"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 transition-all ${isActive('/admin/archive')
                ? 'bg-[var(--pbfs-blue)]/20 text-[var(--pbfs-blue)]'
                : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                }`}
              title="Archive"
          >
              <span className="text-lg">🗄️</span>
              {!isCompact && <span className="text-xs">Archive</span>}
          </Link>
            
            <div className="w-px h-8 bg-white/10"></div>

          <Link
            to="/admin/trash"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 transition-all ${isActive('/admin/trash')
                ? 'bg-red-500/20 text-red-400'
                : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                }`}
              title="Trash"
          >
              <span className="text-lg">🗑️</span>
              {!isCompact && <span className="text-xs">Trash</span>}
          </Link>
          </div>

          {/* Logout Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLogout();
            }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 transition-all text-red-400 hover:text-red-300 font-medium ${isCompact ? 'px-2' : ''}`}
            title="Logout"
          >
              <span className="text-xl">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              </span>
            {!isCompact && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNav;



